import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import FormControlLabel from "@mui/material/FormControlLabel";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import CheckIcon from "@mui/icons-material/Check";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SearchIcon from "@mui/icons-material/Search";
import { isEmpty } from "lodash";

import BaseConfigModal from "@plannel/components/modal/BaseConfigModal";
import StyledSwitch from "@plannel/components/ui/StyledSwitch";
import CodeUtil from "@plannel/pages/utils/code-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import { AUDIT_DIFF_TYPE, auditDiffToText, auditFieldLabel, buildAuditFieldRows, formatAuditTimestamp } from "@plannel/pages/utils/audit-diff-util";
import debug from "@plannel/utils/debug-util";
import { FS } from "@plannel/utils/font-util";

/*
  Audit Trail 변경 상세.

  예전 팝업은 서버 diff 문자열을 공백으로 잘라 토큰마다 한 줄씩 세로로 흘렸다. 이전값과 이후값이
  서로 다른 줄에 떨어져 있어 "무엇이 무엇으로 바뀌었나"를 눈으로 짝지어야 했고, 값에 공백이 하나라도
  있으면 그 짝조차 깨졌다. 여기서는 필드 한 개 = 한 행, 좌=이전 / 우=이후로 고정해 가로로 읽게 한다.

  규격은 config-modal 셸(BaseConfigModal)의 제목·강조선·액션 배치를 따른다. 다만 이 화면은 설정이
  아니라 읽기 전용 상세라 저장 액션이 없어 셸을 그대로 쓰지 않고 같은 톤만 맞췄다.
*/

const { auditEntityNms, auditEventType } = CodeUtil;
const { auditDiffColor, auditOperationColor, tintChipSx } = ColorUtil;

// 필드 / 이전값 / 이후값. 값 두 칸은 같은 폭이라 좌우 대조가 눈에 바로 잡힌다.
// 필드명은 `planSummaryObj.DPCOMPARISON.costAmt` 처럼 중첩 경로가 길어 180px 아래로는 줄바꿈만 늘어난다.
const GRID_TEMPLATE = "minmax(180px, 1fr) minmax(0, 1.4fr) minmax(0, 1.4fr)";
const EMPTY_MARK = "—";
const COPIED_RESET_MS = 1500;

const ROW_ACCENT = {
  [AUDIT_DIFF_TYPE.CHANGED]: auditOperationColor.UPDATE,
  [AUDIT_DIFF_TYPE.ADDED]: auditDiffColor.added,
  [AUDIT_DIFF_TYPE.REMOVED]: auditDiffColor.removed,
};

const ROW_TYPE_LABEL = {
  [AUDIT_DIFF_TYPE.CHANGED]: "modified",
  [AUDIT_DIFF_TYPE.ADDED]: "added",
  [AUDIT_DIFF_TYPE.REMOVED]: "removed",
};

const isBlankValue = (value) => isEmpty(value) || value === "null";

/*
  값 한 칸. 값이 비었거나 null 이면 색을 빼고 —— 로 둔다(빈 칸을 "삭제됨"으로 오독하지 않게).
  틴트는 tintChipSx 와 같은 철학으로 아주 옅게만 — 행이 수십 개면 배경색이 강할수록 먼저 지친다.
*/
const DiffValue = ({ text, color, tinted }) => {
  const blank = isBlankValue(text);

  return (
    <Box sx={{
      px: 1, py: 0.5, borderRadius: 0.75, minWidth: 0, fontSize: FS.TITLE,
      whiteSpace: "pre-wrap", wordBreak: "break-word",
      color: blank ? "text.disabled" : (color ?? "text.primary"),
      bgcolor: tinted && !blank && color ? alpha(color, 0.08) : "transparent",
    }}>
      {blank ? EMPTY_MARK : text}
    </Box>
  );
};

const DiffRow = ({ row, t }) => {
  const unchanged = row.type === AUDIT_DIFF_TYPE.UNCHANGED;
  const accent = ROW_ACCENT[row.type];

  return (
    <Box sx={{
      display: "grid", gridTemplateColumns: GRID_TEMPLATE, alignItems: "start", columnGap: 1,
      borderBottom: 1, borderColor: "divider",
      borderLeft: 3, borderLeftColor: accent ?? "transparent",
      pl: 1, pr: 1, py: 0.5,
      "&:hover": { bgcolor: "action.hover" },
    }}>
      <Tooltip title={unchanged ? "" : t(ROW_TYPE_LABEL[row.type])} placement="left">
        <Box sx={{
          fontSize: FS.TITLE, fontWeight: 600, py: 0.5, minWidth: 0, wordBreak: "break-word",
          color: unchanged ? "text.secondary" : "text.primary",
        }}>
          {auditFieldLabel(row.field)}
        </Box>
      </Tooltip>
      <DiffValue text={row.oldValue} color={unchanged ? undefined : auditDiffColor.removed} tinted={!unchanged} />
      <DiffValue text={row.newValue} color={unchanged ? undefined : auditDiffColor.added} tinted={!unchanged} />
    </Box>
  );
};

const AuditDifference = ({ open, close, selectedData }) => {
  const { t } = useTranslation();

  const [keyword, setKeyword] = useState("");
  const [changedOnly, setChangedOnly] = useState(true);
  const [copied, setCopied] = useState(false);

  const { rows, hasUnchanged } = useMemo(() => buildAuditFieldRows(selectedData), [selectedData]);

  const visibleRows = useMemo(() => {
    const trimmedKeyword = keyword.trim().toLowerCase();

    return rows.filter(row => {
      if (changedOnly && row.type === AUDIT_DIFF_TYPE.UNCHANGED) {
        return false;
      }
      if (isEmpty(trimmedKeyword)) {
        return true;
      }

      // 화면에 보이는 건 라벨("Last Login")이므로 라벨로도 찾혀야 한다.
      // 원래 필드명(lastLoginTs)도 함께 본다 — 개발자는 그쪽으로 검색한다.
      return [auditFieldLabel(row.field), row.field, row.oldValue, row.newValue]
        .some(value => String(value).toLowerCase().includes(trimmedKeyword));
    });
  }, [rows, keyword, changedOnly]);

  // 다시 열 때 앞 사람이 걸어둔 검색어·토글이 남아 "행이 없는" 팝업으로 보이지 않게 초기화한다.
  useEffect(() => {
    if (open) {
      setKeyword("");
      setChangedOnly(true);
      setCopied(false);
    }
  }, [open]);

  const handleClose = () => {
    close(false);
  };

  const copyDiff = () => {
    if (!navigator.clipboard) {
      return;
    }

    navigator.clipboard.writeText(auditDiffToText(visibleRows))
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), COPIED_RESET_MS);
      })
      .catch(debug);
  };

  const { entityId, entityName, operationType, createdUser, createdTs } = selectedData ?? {};
  const entityLabel = auditEntityNms?.[entityName] ?? entityName;
  const operationLabel = auditEventType?.[operationType] ? t(auditEventType[operationType]) : operationType;

  return (
    /* 읽기 전용이라 onSave 가 없다 — 셸이 저장 버튼을 감추고 닫기만 남긴다.
       어떤 레코드를·누가·언제 는 목록으로 돌아가지 않고도 맥락이 잡히도록 subtitle 한 줄로. */
    <BaseConfigModal
      open={open}
      onClose={handleClose}
      title={entityLabel ?? t("changeDetail")}
      titleAdornment={operationType
        ? <Chip size="small" label={operationLabel} sx={tintChipSx(auditOperationColor[operationType])} />
        : null}
      subtitle={[
        entityId ? `${t("entityId")} ${entityId}` : null,
        createdUser,
        createdTs ? `${formatAuditTimestamp(createdTs)} (UTC)` : null,
      ].filter(Boolean).join("  ·  ")}
      cancelLabel={t("close")}
      extraActions={
        <Button size="small" disabled={isEmpty(visibleRows)}
          startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />} onClick={copyDiff}>
          {t("copy")}
        </Button>
      }
      width={1200}
      paperSx={{ maxHeight: "85vh" }}>

      <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
        <TextField size="small" value={keyword} placeholder={t("search")}
          onChange={(event) => setKeyword(event.target.value)}
          sx={{ width: 240 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: "text.disabled" }} />
              </InputAdornment>
            ),
          }} />
        {hasUnchanged && (
          <FormControlLabel
            control={<StyledSwitch size="small" sx={{ mr: 2 }} checked={changedOnly}
              onChange={(event) => setChangedOnly(event.target.checked)} />}
            label={<Typography sx={{ fontSize: FS.TITLE }}>{t("changedOnly")}</Typography>} />
        )}
        <Typography sx={{ ml: "auto", fontSize: FS.TITLE, color: "text.secondary" }}>{visibleRows.length}</Typography>
      </Box>

      <Box sx={{ mt: 1 }}>
        {isEmpty(rows) && (
          <Typography sx={{ py: 3, textAlign: "center", color: "text.secondary", fontSize: FS.TITLE }}>
            {t("MSG_NoChangeData")}
          </Typography>
        )}
        {!isEmpty(rows) && (
          <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1.5, overflow: "hidden" }}>
            <Box sx={{
              display: "grid", gridTemplateColumns: GRID_TEMPLATE, columnGap: 1,
              position: "sticky", top: 0, zIndex: 1,
              bgcolor: "grey.50", borderBottom: 1, borderColor: "divider",
              pl: 1.375, pr: 1, py: 0.75,
              fontSize: FS.SUB, fontWeight: 700, color: "text.secondary",
            }}>
              <Box>{t("field")}</Box>
              <Box sx={{ pl: 1 }}>{t("oldValue")}</Box>
              <Box sx={{ pl: 1 }}>{t("newValue")}</Box>
            </Box>
            {visibleRows.map(row => <DiffRow key={`${row.type}-${row.field}`} row={row} t={t} />)}
            {isEmpty(visibleRows) && (
              <Typography sx={{ py: 3, textAlign: "center", color: "text.secondary", fontSize: FS.TITLE }}>
                {t("MSG_NoData")}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </BaseConfigModal>
  );
};

export default AuditDifference;
